// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Durable Amy turns (ADR-079, 2026-09-16) — ONE status record per turn so a
 * client that lost its stream (the user switched apps) recovers from the
 * server's truth, never from a socket's mood.
 *
 * Status only: the answer text lives in AgentCore Memory and is read through
 * `getAmyHistory` (ADR-045 — one answer store; export/erase paths untouched).
 * `expiresAt` is a SERVED deadline (started_at + cap + grace) so clients never
 * carry the cap constant. `error.code` is a stable code, never a message.
 *
 * Written by the RUNTIME through the API with the caller's own bearer
 * (ADR-019): `registerAmyTurn` at turn start, `completeAmyTurn` at turn end.
 * Read by the clients: `getAmyTurn` on stream loss / foreground / focus.
 */
import { z } from "zod";
import { IsoDateTimeSchema } from "./primitives.js";
export const AmyTurnStatusSchema = z.enum(["running", "completed", "failed", "abandoned"]);
/** A stable code only (e.g. TURN_CAP_EXCEEDED, TURN_ORPHANED, CONVERSATION_RESET, an exception class name) — never a message. */
export const AmyTurnErrorSchema = z.object({ code: z.string().min(1).max(64) });
export const AmyTurnSchema = z.object({
    /** The turn id — a v4 uuid, client-minted (runtime-minted for legacy payloads). */
    id: z.uuid(),
    /** The durable conversation this turn belongs to (ADR-045). */
    conversationId: z.string().min(1).max(256),
    status: AmyTurnStatusSchema,
    startedAt: IsoDateTimeSchema,
    finishedAt: IsoDateTimeSchema.nullable(),
    /** startedAt + cap + grace: after this, a still-`running` row is finalized as `abandoned` on read. */
    expiresAt: IsoDateTimeSchema,
    /** true only once the assistant message is in Memory — the moment "answered" becomes an honest word. */
    answerRecorded: z.boolean(),
    error: AmyTurnErrorSchema.nullable(),
});
/** GET /v1/amy/turns/{id} and PATCH /v1/amy/turns/{id} both answer with the turn. */
export const AmyTurnResponseSchema = AmyTurnSchema;
/** POST /v1/amy/turns — the runtime registers the turn it is about to run. */
export const AmyTurnRegisterRequestSchema = z.object({
    turnId: z.uuid(),
    conversationId: z.string().min(1).max(256),
    /** AgentCore runtime session id (≥ 33 chars, ADR-040). */
    runtimeSessionId: z.string().min(33).max(256),
    client: z.enum(["web", "mobile"]).nullable().optional(),
    clientBuild: z.string().max(128).nullable().optional(),
});
/** `created: false` = the row already existed (a resend racing the client's own status check) — the runtime then emits `turn_duplicate` and runs nothing. */
export const AmyTurnRegisterResponseSchema = z.object({ turn: AmyTurnSchema, created: z.boolean() });
/** PATCH /v1/amy/turns/{id} — the runtime records the terminal status. Guarded server-side: only a `running` row transitions. */
export const AmyTurnCompleteRequestSchema = z.object({
    status: z.enum(["completed", "failed", "abandoned"]),
    answerRecorded: z.boolean(),
    /** Phase-2 hook: true = the consumer took the terminal event; false = gone before it; null = unknown. */
    clientConnected: z.boolean().nullable(),
    error: AmyTurnErrorSchema.nullable().optional(),
});
//# sourceMappingURL=amyTurns.js.map